import {CUSTOM_ELEMENTS_SCHEMA, NgModule} from "@angular/core";
import {CommonModule, DatePipe} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {ToolbarMenuComponent} from "./shared-components/menue/toolbar-menu.component";
import {UserImageInitialsComponent} from "./shared-components/user-image-initials/user-image-initials.component";
import {UserImagePipe} from "./shared-pipes/user-image.pipe";
import {AudioPlayerComponent} from "./shared-components/audio-player/audio-player.component";
import {CameraViewModeSelectorComponent} from "./shared-components/camera-view-mode-selector/camera-view-mode-selector.component";
import {RouterModule} from "@angular/router";
import {IpBeautifySpacesPipe} from "./shared-pipes/ip-beautify-spaces.pipe";
import {FocusDirective} from "./custom-directives/focus/focus.directive";
import {MediaPlayDirective} from "./custom-directives/media-directives/play.directive";
import {LayoutModule} from "@angular/cdk/layout";
import {OldPaginationComponent} from "./shared-components/pagination/old-pagination.component";
import {TrainingRoomNavigationPanelComponent} from "./shared-components/training-room-panels/training-room-navigation-panel/training-room-navigation-panel.component";
import {SlideToggleDirective} from "./custom-directives/animations/slide-toggle/slide-toggle.directive";
import {NotFoundComponent} from "./shared-components/display-error-utils/not-found/not-found.component";
import {IconImageComponent} from "./shared-components/icon-image/icon-image.component";
import {MmssPipe} from "./shared-pipes/mmss.pipe";
import {MinsecPipe} from "./shared-pipes/minsec.pipe";
import {ChecklistPaneComponent} from "./shared-components/checklist-pane/checklist-pane.component";
import {BookmarkPaneComponent} from "./shared-components/bookmark-pane/bookmark-pane-normal/bookmark-pane.component";
import {DurationLineContainerComponent} from "./shared-components/duration-line-controls/duration-line-container/duration-line-container.component";
import {DurationLineNormalComponent} from "./shared-components/duration-line-controls/duration-line-normal/duration-line-normal.component";
import {DurationLineReversedComponent} from "./shared-components/duration-line-controls/duration-line-reversed/duration-line-reversed.component";
import {DebugContainerComponent} from "./utilities/debug-container/debug-container.component";
import {FadeToggleDirective} from "./custom-directives/animations/fadeToggle/fade-toggle.directive";
import {AudioViewerComponent} from "./shared-components/media-viewers/audio-viewer/audio-viewer.component";
import {DocumentViewerComponent} from "./shared-components/media-viewers/document-viewer/document-viewer.component";
import {PresentationViewerComponent} from "./shared-components/media-viewers/presentation-viewer/presentation-viewer.component";
import {CameraViewerComponent} from "./shared-components/media-viewers/camera-viewer/camera-viewer.component";
import {ImageViewerComponent} from "./shared-components/media-viewers/image-viewer/image-viewer.component";
import {MediaViewerComponent} from "./shared-components/media-viewers/media-viewer.component";
import {OdpViewerComponent} from "./shared-components/media-viewers/presentation-viewer/components/odp-viewer/odp-viewer.component";
import {PptxViewerComponent} from "./shared-components/media-viewers/presentation-viewer/components/pptx-viewer/pptx-viewer.component";
import {DigitalSignageViewerComponent} from "./shared-components/digital-signage-viewers/digital-signage-viewer/digital-signage-viewer.component";
import {ClinicalVignetteComponent} from "./shared-components/clinical-vignette/clinical-vignette.component";
import {WelcomeComponent} from "./shared-components/welcome/welcome.component";
import {VideoViewerComponent} from "./shared-components/media-viewers/video-viewer/video-viewer.component";
import {LoadingDotsComponent} from "./shared-components/loading-dots/loading-dots.component";
import {MaterialModule} from "./modules/material.module";
import {SlidePanelComponent} from "./shared-components/slide-panel/slide-panel.component";
import {TrainingRoomButtonComponent} from "./shared-components/training-room-panels/training-room-navigation-panel/training-room-button/training-room-button.component";
import {SlideNavigationPanelComponent} from "./shared-components/slide-navigation-panel/slide-navigation-panel.component";
import {FilterSessionByDatePipe} from "./shared-pipes/filter-session-by-date.pipe";
import {AmplitudeDirective} from "./custom-directives/amplitude/amplitude.directive";
import {AxisMediaStreamLibraryVideoComponent} from "./shared-components/recorder-camera/controls/axis-media-stream-library-video/axis-media-stream-library-video.component";
import {MjpegImageComponent} from "./shared-components/recorder-camera/controls/mjpeg-image/mjpeg-image.component";
import {RecorderCameraComponent} from "./shared-components/recorder-camera/recorder-camera.component";
import {BaseCameraLayout} from "./shared-components/recorder-layout/recorder-layout.component";
import {RecorderLayoutSingleComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-single/recorder-layout-single.component";
import {RecorderLayoutTwinComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-twin/recorder-layout-twin.component";
import {RecorderLayoutSixteenGridComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-sixteen-grid/recorder-layout-sixteen-grid.component";
import {RecorderLayoutBsssComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-bsss/recorder-layout-bsss.component";
import {RecorderLayoutPipComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-pip/recorder-layout-pip.component";
import {RecorderLayoutQuadComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-quad/recorder-layout-quad.component";
import {RecorderLayoutBssComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-bss/recorder-layout-bss.component";
import {SimSlideToggleIfElseComponent} from "./shared-components/sim-slide-toggle-if-else/sim-slide-toggle-if-else.component";
import {OldPanelComponent} from "./shared-components/panel-components/old-panel/old-panel.component";
import {MarkerFilterPipe} from "./shared-pipes/marker-filter.pipe";
import {MediaFilterPipe} from "./shared-pipes/media-filter.pipe";
import {CameraViewModeToIndexPipe} from "./shared-pipes/camera-view-mode-to-index.pipe";
import {CastToAudioInputChannelsPipe} from "./shared-pipes/cast-to-audio-input-channels.pipe";
import {MarkerSetPaneComponent} from "./shared-components/marker-set-pane/marker-set-pane.component";
import {RecorderLayoutTwobthreeComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-twobthree/recorder-layout-twobthree.component";
import {RecorderLayoutTbtComponent} from "./shared-components/recorder-layout/layouts/recorder-layout-tbt/recorder-layout-tbt.component";
import {ScreenContainerComponent} from "./shared-components/recorder-layout/layouts/screen-container.component";
import {AppTileComponent} from "./shared-components/app-tile/app-tile.component";
import {EventDetailSessionComponent} from "./shared-components/event-detail-session/event-detail-session.component";
import {SimFilterPipe} from "./shared-pipes/sim-filter.pipe";
import {UserFullNameComponent} from "./shared-components/user-full-name/user-full-name.component";
import {RoundPipe} from "./shared-pipes/round.pipe";
import {DynamicScriptDirective} from "./custom-directives/dynamic-script.directive";
import {RecordingTimerComponent} from "./shared-components/recording-timer/recording-timer.component";
import {EventSelectionComponent} from "./shared-components/event-selection/event-selection.component";
import {RecordingArchiveCameraComponent} from "./shared-components/recording-archive-camera/recording-archive-camera.component";
import {ScenarioRecordingPlayerComponent} from "./shared-components/scenario-recording-player/scenario-recording-player.component";
import {FileUploadComponent} from "./shared-components/file-upload/file-upload.component";
import {OrderByPipe} from "./shared-pipes/orderby.pipe";
import {DictionaryPipe} from "./shared-pipes/dictionary.pipe";
import {AudioChannelControlPanelComponent} from "./shared-components/audio-channel-control-panel/audio-channel-control-panel.component";
import {TabletAudioChannelControlPanelComponent} from "./shared-components/audio-channel-control-panel/tablet-audio-channel-control-panel.component";
import {FilterEmptyScenarioRecordingsPipe} from "./shared-pipes/filter-empty-scenario-recordings.pipe";
import {FilterEmptyGroupedParticipantPipe} from "./shared-pipes/filter-empty-grouped-participant.pipe";
import {NotnullPipe} from "./shared-pipes/notnull.pipe";
import {MultiSwitchCaseDirective} from "./custom-directives/multi-switch-case.directive";
import {OldSideActionBarComponent} from "./shared-components/side-action-bar/old-side-action-bar.component";
import {CustomDateAdapter, EventCalendarComponent} from "./shared-components/event-calendar/event-calendar.component";
import {CustomHeaderCalendarComponent} from "./shared-components/event-calendar/custom-header-calendar/custom-header-calendar.component";
import {SystemActionsComponent} from "./shared-components/system-actions/system-actions.component";
import {MediaCardImageComponent} from "./shared-components/media-library-panel/media-card-image/media-card-image.component";
import {MediaCardAudioComponent} from "./shared-components/media-library-panel/media-card-audio/media-card-audio.component";
import {MediaCardPowerPointComponent} from "./shared-components/media-library-panel/media-card-power-point/media-card-power-point.component";
import {MediaCardVideoComponent} from "./shared-components/media-library-panel/media-card-video/media-card-video.component";
import {MediaCardPdfComponent} from "./shared-components/media-library-panel/media-card-pdf/media-card-pdf.component";
import {MediaLibraryPanelComponent} from "./shared-components/media-library-panel/media-library-panel.component";
import {WebSocketDatePipe} from "./shared-pipes/web-socket-date.pipe";
import {VarDirective} from "./custom-directives/ng-var.directive";
import {RtspwebrtcVideoComponent} from "./shared-components/recorder-camera/controls/rtspwebrtc-video/rtspwebrtc-video/rtspwebrtc-video.component";
import {MseVideoComponent} from "./shared-components/recorder-camera/controls/mse-video/mse-video.component";
import {NgxMaterialTimepickerModule} from "ngx-material-timepicker";
import {AddLeadingZeroPipe} from "./shared-pipes/add-leading-zero.pipe";
import {WrapFnPipe} from "./shared-pipes/function-to-pipe.pipe";
import {AudioInputDeviceComponent} from "./shared-components/audio-input-device/audio-input-device.component";
import {GetScenarioRecordingByIdPipe} from "./shared-pipes/get-scenario-recording-by-id.pipe";
import {EventContainerComponent} from "./shared-components/event-container/event-container.component";
import {GetRoomCapabilityStringPipe} from "./shared-pipes/get-room-capability-string.pipe";
import {GetScenarioGroupNameByScenarioIdPipe} from "./shared-pipes/get-scenario-group-name-by-scenario-id.pipe";
import {GetMarkerSetByIdPipe} from "./shared-pipes/get-marker-set-by-id.pipe";
import {GetVogSetByIdPipe} from "./shared-pipes/get-vog-set-by-id.pipe";
import {GetVopSetByIdPipe} from "./shared-pipes/get-vop-set-by-id.pipe";
import {GetChecklistByIdPipe} from "./shared-pipes/get-checklist-by-id.pipe";
import {GetScenarioMediaSetByIdPipe} from "./shared-pipes/get-scenario-media-set-by-id.pipe";
import {MediaCardListComponent} from "./shared-components/media-card-list/media-card-list.component";
import {DetachableChangeDetectionDirective} from "./custom-directives/detachable-change-detection.directive";
import {BasicAdministrationMediaLibraryEditPageComponent} from "./shared-components/basic-administration-media-library-edit-page/basic-administration-media-library-edit-page.component";
import {GetEventStateStringForCustomerPipe} from "./shared-pipes/get-event-state-string-for-customer.pipe";
import {ArrayFilterPipe} from "./shared-pipes/array-filter.pipe";
import {AudioChannelFilterPipe} from "./shared-pipes/audio-channel-filter.pipe";
import {AttachmentItemComponent} from "./shared-components/attachment-item/attachment-item.component";
import {GetEventByScenarioRecordingIdPipe} from "./shared-pipes/get-event-by-scenario-recording-id.pipe";
import {GetTrainingSessionbyIdPipe} from "./shared-pipes/get-training-sessionby-id.pipe";
import {GetUserByIdPipe} from "./shared-pipes/get-user-by-id.pipe";
import {RoomLayoutSwitcherComponent} from "./shared-components/room-layout-switcher/room-layout-switcher.component";
import {IsDefaultIconPipe} from "./shared-pipes/is-default-icon.pipe";
import {DateAdapter} from "@angular/material/core";
import {GetCameraByIdPipe} from "./shared-pipes/get-camera-by-id.pipe";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {MatPaginatorLocalization} from "./types/mat-paginator-localization";
import {GetEventStateStringPipe} from "./shared-pipes/get-event-state-string.pipe";
import {BasicAdminSettingsTableComponent} from "./shared-components/basic-admin-settings-table/basic-admin-settings-table.component";
import {TypedMatCellDefDirective} from "./custom-directives/customMatCellDef/typed-mat-cell-def.directive";
import {SimAdminSettingsInputToLabelTransformerComponent} from "./shared-components/sim-admin-settings-input-to-label-transformer/sim-admin-settings-input-to-label-transformer.component";
import {NgxJsonViewerModule} from "ngx-json-viewer";
import {SimAdminSettingsSelectToLabelTransformerComponent} from "./shared-components/sim-admin-settings-select-to-label-transformer/sim-admin-settings-select-to-label-transformer.component";
import {SimAdminSettingsInputListToEditableListComponent} from "./shared-components/sim-admin-settings-input-list-to-editable-list/sim-admin-settings-input-list-to-editable-list.component";
import {SimAdminSettingsSliderToEditableSliderComponent} from "./shared-components/sim-admin-settings-slider-to-editable-slider/sim-admin-settings-slider-to-editable-slider.component";
import {ScenarioArchiveExportsPanelComponent} from "./shared-components/scenario-archive-exports-panel/scenario-archive-exports-panel.component";
import {SimBaseChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-base-chip-input/sim-base-chip-input.component";
import {SimUserChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-user-chip-input/sim-user-chip-input.component";
import {SimRoomChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-room-chip-input/sim-room-chip-input.component";
import {SimScenarioChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-scenario-chip-input/sim-scenario-chip-input.component";
import {SimRoomGroupChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-room-group-chip-input/sim-room-group-chip-input.component";
import {SimBookmarksChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-bookmarks-chip-input/sim-bookmarks-chip-input.component";
import {SimChecklistChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-checklist-chip-input/sim-checklist-chip-input.component";
import {SimScenarioMediaChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-scenario-media-chip-input/sim-scenario-media-chip-input.component";
import {SimVogChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-vog-chip-input/sim-vog-chip-input.component";
import {SimVopChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-vop-chip-input/sim-vop-chip-input.component";
import {FileSizePipe} from "./shared-pipes/file-size.pipe";
import {CountdownTimerComponent} from "./shared-components/timer/countdown-timer.component";
import {SimScenarioGroupChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-scenario-group-chip-input/sim-scenario-group-chip-input.component";
import {FilterInvisibleAudioChannelsPipe} from "./shared-pipes/filter-invisible-audio-channels.pipe";
import {BaseAdministrationCheckedActionTableComponent} from "./shared-components/base-administration-checked-action-table/base-administration-checked-action-table.component";
import {GetTrainingRoomGroupByIdPipe} from "./shared-pipes/get-training-room-group-by-id.pipe";
import {ChecklistItemComponent} from "./shared-components/assessable-panels/checklist-item/checklist-item.component";
import {TableVirtualScrollModule} from "ng-table-virtual-scroll";
import {RoomLegendDisplayComponent} from "./shared-components/room-legend-display/room-legend-display.component";
import {UnsupportedTabletTemplateComponent} from "./shared-components/unsupported-tablet-template/unsupported-tablet-template.component";
import {FullCalendarModule} from "@fullcalendar/angular";
import {LabeledInfoComponent} from "./shared-components/labeled-info/labeled-info.component";
import {FilterDefaultBookmarksPipe} from "./shared-pipes/filter-default-bookmarks.pipe";
import {BatteryStatusIconComponent} from "./shared-components/battery-status-icon/battery-status-icon.component";
import {SimAdminSettingsEnumMultipleSelectToLabelTransformerComponent} from "./shared-components/sim-admin-settings-enum-multiple-select-to-label-transformer/sim-admin-settings-enum-multiple-select-to-label-transformer.component";
import {BatteryStateComponent} from "./shared-components/battery-state/battery-state.component";
import {AssessableGroupPanelComponent} from "./shared-components/assessable-panels/assessable-group-panel/assessable-group-panel.component";
import {AssessmentItemComponent} from "./shared-components/assessable-panels/assessment-item/assessment-item.component";
import {AssessmentPointInputComponent} from "./shared-components/assessable-panels/assessment-point-input/assessment-point-input.component";
import {AssessmentPaneComponent} from "./shared-components/assessment-pane/assessment-pane.component";
import {ArrayWithoutPipe} from "./shared-pipes/array-without.pipe";
import {TableColumnSearchComponent} from "./shared-components/table-column-search/table-column-search.component";
import {QuillModule} from "ngx-quill";
import {RichTextEditorControlComponent} from "./shared-components/rich-text-editor/rich-text-editor-control/rich-text-editor-control.component";
import {ScenarioArchiveFilesetsPanelComponent} from "./shared-components/scenario-archive-filesets-panel/scenario-archive-filesets-panel.component";
import {GetEventByIdPipe} from "./shared-pipes/get-event-by-id.pipe";
import {ChecklistPanelComponent} from "./shared-components/assessable-panels/checklist-panel/checklist-panel.component";
import {SimEventChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-event-chip-input/sim-event-chip-input.component";
import {GetUsersByIdsPipe} from "./shared-pipes/get-users-by-ids.pipe";
import {HorizontalUserBubblesComponent} from "./shared-components/horizontal-user-bubbles/horizontal-user-bubbles.component";
import {SimAssessmentTemplateChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-assessemnt-template-chip-input/sim-assessment-template-chip-input.component";
import {OperatorChecklistPanelComponent} from "./shared-components/assessable-panels/operator-checklist-panel/operator-checklist-panel.component";
import {SimTagsChipInputComponent} from "./shared-components/sim-advanced-inputs/sim-tags-chip-input/sim-tags-chip-input.component";
import {SimAdminSettingsInputChipListComponent} from "./shared-components/sim-admin-settings-input-chip-list/sim-admin-settings-input-chip-list.component";
import {SimTagsChipViewComponent} from "./shared-components/sim-advanced-inputs/sim-tags-chip-view/sim-tags-chip-view.component";
import {TrainingRoomPlanViewComponent} from "./shared-components/training-room-panels/training-room-navigation-panel/training-room-plan-view/training-room-plan-view.component";
import {AssessableAdminIndexComponent} from "./shared-components/assessable-panels/assessable-admin-index/assessable-admin-index.component";
import {ThreeColumnLayoutComponent} from "./shared-components/three-column-layout/three-column-layout.component";
import {MultipleChoiceItemComponent} from "./shared-components/assessable-panels/multiple-choice-item/multiple-choice-item.component";
import {SingleChoiceItemComponent} from "./shared-components/assessable-panels/single-choice-item/single-choice-item.component";
import {AssessableItemComponent} from "./shared-components/assessable-panels/assessable-item/assessable-item.component";
import {YesNoItemComponent} from "./shared-components/assessable-panels/yes-no-item/yes-no-item.component";
import {RatingItemComponent} from "./shared-components/assessable-panels/rating-item/rating-item.component";
import {FreeAnswerItemComponent} from "./shared-components/assessable-panels/free-answer-item/free-answer-item.component";
import {NoteAreaComponent} from "./shared-components/assessable-panels/note-area/note-area.component";
import {SimAutocompleteComponent} from "./shared-components/sim-advanced-inputs/autocomplete/sim-autocomplete/sim-autocomplete.component";
import {OperatorPanelComponent} from "./shared-components/operator-panel/operator-panel.component";
import {AssessmentExaminerNoteComponent} from "./shared-components/assessable-panels/assessment-examiner-note/assessment-examiner-note.component";
import {NgAsDirective, NgAsPipe} from "ng-as";
import {OnClickModule} from "./shared-components/rich-text-editor/quill-image-popup-module/OnClickModule";
import {RichTextEditorViewComponent} from "./shared-components/rich-text-editor/rich-text-editor-view/rich-text-editor-view.component";
import {StripHtmlPipe} from "./shared-pipes/strip-html.pipe";
import {SafeHtmlComponent} from "./shared-components/safe-html/safe-html.component";
import {SimAdminSettingsScrollEntriesConComponent} from "./shared-components/sim-admin-settings-scroll-entries-con/sim-admin-settings-scroll-entries-con.component";
import {SimChipAutocompleteComponent} from "./shared-components/sim-advanced-inputs/autocomplete/sim-chip-autocomplete/sim-chip-autocomplete.component";
import {GetExamFlowByIdPipe} from "./shared-pipes/get-exam-flow-by-id.pipe";
import {SimDurationInputComponent} from "@common/ui/sim-duration-input/sim-duration-input.component";
import {EventStatusWidgetComponent} from "./shared-components/event-status-widget/event-status-widget.component";
import {AssessableAnswerItemComponent} from "./shared-components/assessable-panels/assessable-answer-item/assessable-answer-item.component";
import {ItemScoreComponent} from "./shared-components/assessable-panels/item-score/item-score.component";
import {AssessableGroupComponent} from "./shared-components/assessable-panels/assessable-group-panel/components/assessable-group/assessable-group.component";
import {AssessmentEditPanelComponent} from "./shared-components/assessable-panels/assessment-edit-panel/assessment-edit-panel.component";
import {AssessmentAnswerPanelComponent} from "./shared-components/assessable-panels/assessment-answer-panel/assessment-answer-panel.component";
import {CompetenceCategoryEditComponent} from "./shared-components/assessable-panels/competence-category-edit/competence-category-edit.component";
import {CompetenceCategoryAnswerComponent} from "./shared-components/assessable-panels/competence-category-answer/competence-category-answer.component";
import {ClinicalVignetteViewComponent} from "./shared-components/clinical-vignette-view/clinical-vignette-view.component";
import {DrawerFormFieldComponent} from "./shared-components/sim-advanced-inputs/drawer-form-field/drawer-form-field.component";
import {OldParticipantDetailsComponent} from "./shared-components/participant-details/old-participant-details.component";
import {AssessmentAnswerSingleItemPanelComponent} from "./shared-components/assessable-panels/assessment-answer-single-item-panel/assessment-answer-single-item-panel.component";
import {ClockTimeInputComponent} from "./shared-components/sim-advanced-inputs/sim-time-input/sim-time-input/clock-time-input.component";
import {TwoColumnLayoutComponent} from "./shared-components/two-column-layout/two-column-layout.component";
import {GetUserRecordingAssessmentByIdPipe} from "./shared-pipes/get-user-recording-assessment-by-id.pipe";
import {ParticipantDeviceExaminerAssessmentDisplayComponent} from "./shared-components/participant-device-examiner-assessment-display/participant-device-examiner-assessment-display.component";
import {ParticipantDeviceStudentAssessmentDisplayComponent} from "./shared-components/participant-device-student-assessment-display/participant-device-student-assessment-display.component";
import {NumpadComponent} from "./shared-components/sim-advanced-inputs/numpad/numpad.component";
import {AdministrationTableComponent} from "./shared-components/administration-table/administration-table/administration-table.component";
import {AdministrationTableBulkActionsComponent} from "./shared-components/administration-table/administration-table-bulk-actions/administration-table-bulk-actions.component";
import {AdministrationTablePaginatorComponent} from "./shared-components/administration-table/administration-table-paginator/administration-table-paginator.component";
import {AdministrationTableAdvancedSearchComponent} from "./shared-components/administration-table/administration-table-advanced-search/administration-table-advanced-search.component";
import {AdministrationTableAppliedFiltersComponent} from "./shared-components/administration-table/administration-table-applied-filters/administration-table-applied-filters.component";
import {AdministrationTablePageLayoutComponent} from "./shared-components/page-layout-wrapper-components/administration-table-page-layout/administration-table-page-layout.component";
import {PhaseNavButtonComponent} from "./shared-components/phase-nav-button/phase-nav-button.component";
import {AdministrationTableSimpleSearchComponent} from "./shared-components/administration-table/administration-table-simple-search/administration-table-simple-search.component";
import {TimeInputComponent} from "./shared-components/sim-advanced-inputs/sim-duration-input/time-input/time-input.component";
import {ClientAudioDeviceComponent} from "./shared-components/menue/client-audio-device/client-audio-device/client-audio-device.component";
import {FileListItemComponent} from "@common/ui/list-item/file-list-item/file-list-item.component";
import {NewListItemComponent} from "@common/ui/list-item/new-list-item/new-list-item.component";
import {UserListItemComponent} from "@common/ui/list-item/user-list-item/user-list-item.component";
import {EventDaySelectorComponent} from "./shared-components/date-tab-selector/event-day-selector.component";
import {EventStatusTagComponent} from "./shared-components/event-status-tag/event-status-tag.component";
import {TimerDisplayComponent} from "./shared-components/timer-display/timer-display.component";
import {TypeGuardPipe} from "./shared-pipes/type-guard.pipe";
import {AssessableItemTemplateDirective} from "./shared-components/assessable-panels/assessable-item-template.directive";
import {EnumLabelPipe} from "./shared-pipes/enum-label.pipe";
import {BasicPageLayoutComponent} from "./shared-components/page-layout-wrapper-components/basic-page-layout/basic-page-layout.component";
import {ComponentLoadingContainerComponent} from "./shared-components/component-loading-container/component-loading-container.component";
import {LogoComponent} from "./shared-components/logo/logo.component";
import {ListItemComponent} from "@common/ui/list-item/list-item.component";
import {SessionCardComponent} from "@common/components/session/session-card/session-card.component";
import {GetScenarioByIdPipe} from "@common/pipes/get-scenario-by-id.pipe";
import {SimButton} from "@common/ui/button/sim-button.component";
import {GetScenarioRecordingStateStringForCustomerPipe} from "@common/pipes/get-scenario-recording-state-string-for-customer.pipe";
import {AnimatedMaterialIconComponent} from "@common/ui/animated-material-icon/animated-material-icon.component";
import {FilterParticipantsPipe} from "@common/pipes/filter-participants.pipe";
import {BusyDirective} from "@common/directives/busy.directive";
import {GetRecordingMappingIdPipe} from "@common/pipes/get-recording-mapping-id.pipe";
import {HasDataPermissionDirective} from "@common/directives/perm-directives/has-data-permission.directive";
import {HasPermissionDirective} from "@common/directives/perm-directives/has-permission.directive";
import {TransformParticipantsToSimUsersPipe} from "@common/pipes/transform-participants-to-sim-users.pipe";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";
import {ExamDevicePanelComponent} from "@common/components/exam-device-panel/exam-device-panel.component";
import {AssessmentExamIndexComponent} from "@common/components/assessment/assessment-exam-index/assessment-exam-index.component";
import {AssessmentExamInfoHeaderComponent} from "@common/components/assessment/assessment-exam-info-header/assessment-exam-info-header.component";
import {LabelledContainerComponent} from "@common/ui/labelled-container/labelled-container.component";
import {ActionMenuComponent} from "@common/ui/action-menu/action-menu.component"; // Echarts setup
import {NgxEchartsModule} from "ngx-echarts";
import {CanvasRenderer} from "echarts/renderers";
import {BarChart, GraphChart, PieChart} from "echarts/charts";
import {GridComponent, LegendComponent, TooltipComponent} from "echarts/components";
import * as echarts from "echarts/core";
import {SideActionBarComponent} from "@common/ui/side-action-bar/side-action-bar.component";
import {EnvironmentOverlayComponent} from "./shared-components/environment-overlay/environment-overlay.component";
import {LmsPublishControlComponent} from "./shared-components/lms-publish-control/lms-publish-control.component";
import {SegmentedButtonComponent} from "@common/ui/segmented-button/segmented-button.component";
import {ButtonSegmentComponent} from "@common/ui/segmented-button/button-segment/button-segment.component";
import {TimeRangeLabelPipe} from "@common/pipes/time/time-range-label.pipe";
import {DurationLabelPipe} from "@common/pipes/time/duration-label.pipe";
import {PanelContentWarningContainerComponent} from "@common/components/panels/panel-content-warning-container/panel-content-warning-container.component";
import {AudioElementComponent} from "@common/ui/audio-element/audio-element.component";
import {MarqueeTextComponent} from "@common/ui/marquee-text/marquee-text.component";
import {RecordingStatusTagComponent} from "@common/components/recording-status-tag/recording-status-tag.component";
import {ScheduledSoundOffsetPipe} from "@common/pipes/time/offset-duration.pipe";

echarts.use([CanvasRenderer, BarChart, PieChart, GraphChart, GridComponent, LegendComponent, TooltipComponent]);

function createMatPaginatorLocalization(translateService: TranslateService) {
	return new MatPaginatorLocalization(translateService);
}

/**
 * SharedModule
 * Imports major libraries and modules makes simple app configs
 *
 * Declararion of global used conponents, pipes, services and assets
 */
@NgModule({
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	declarations: [
		EventStatusTagComponent,
		FileListItemComponent,
		ClientAudioDeviceComponent,
		TimeInputComponent,
		PhaseNavButtonComponent,
		NumpadComponent,
		MediaCardImageComponent,
		MediaCardAudioComponent,
		MediaCardPowerPointComponent,
		MediaCardVideoComponent,
		MediaCardPdfComponent,
		MediaLibraryPanelComponent,
		MarkerSetPaneComponent,
		RecordingArchiveCameraComponent,
		AddLeadingZeroPipe,
		RecordingTimerComponent,
		CustomHeaderCalendarComponent,
		DynamicScriptDirective,
		RoundPipe,
		UserFullNameComponent,
		EventDetailSessionComponent,
		EventCalendarComponent,
		AppTileComponent,
		RecorderLayoutTbtComponent,
		ScreenContainerComponent,
		RecorderLayoutTwobthreeComponent,
		RecorderLayoutTwinComponent,
		RecorderLayoutSixteenGridComponent,
		RecorderLayoutSingleComponent,
		RecorderLayoutQuadComponent,
		RecorderLayoutPipComponent,
		RecorderLayoutBsssComponent,
		RecorderLayoutBssComponent,
		BaseCameraLayout,
		RecorderCameraComponent,
		MjpegImageComponent,
		AxisMediaStreamLibraryVideoComponent,
		OrderByPipe,
		ToolbarMenuComponent,
		UserImageInitialsComponent,
		UserImagePipe,
		SlideNavigationPanelComponent,
		AudioPlayerComponent,
		CameraViewModeSelectorComponent,
		IpBeautifySpacesPipe,
		FocusDirective,
		MediaPlayDirective,
		SimFilterPipe,
		TrainingRoomNavigationPanelComponent,
		SlideToggleDirective,
		NotFoundComponent,
		IconImageComponent,
		MmssPipe,
		MinsecPipe,
		ChecklistPaneComponent,
		BookmarkPaneComponent,
		DurationLineContainerComponent,
		DurationLineNormalComponent,
		DurationLineReversedComponent,
		DebugContainerComponent,
		FadeToggleDirective,
		AudioViewerComponent,
		DocumentViewerComponent,
		PresentationViewerComponent,
		CameraViewerComponent,
		ImageViewerComponent,
		MediaViewerComponent,
		OdpViewerComponent,
		PptxViewerComponent,
		DigitalSignageViewerComponent,
		ClinicalVignetteComponent,
		WelcomeComponent,
		VideoViewerComponent,
		LoadingDotsComponent,
		SlidePanelComponent,
		TrainingRoomButtonComponent,
		TrainingRoomPlanViewComponent,
		SlideNavigationPanelComponent,
		FilterSessionByDatePipe,
		AmplitudeDirective,
		SimSlideToggleIfElseComponent,
		OldPanelComponent,
		MarkerFilterPipe,
		MediaFilterPipe,
		CameraViewModeToIndexPipe,
		CastToAudioInputChannelsPipe,
		EventSelectionComponent,
		ScenarioRecordingPlayerComponent,
		FileUploadComponent,
		AudioChannelControlPanelComponent,
		TabletAudioChannelControlPanelComponent,
		FilterEmptyScenarioRecordingsPipe,
		FilterEmptyGroupedParticipantPipe,
		NotnullPipe,
		MultiSwitchCaseDirective,
		OldSideActionBarComponent,
		SystemActionsComponent,
		EnvironmentOverlayComponent,
		WebSocketDatePipe,
		VarDirective,
		RtspwebrtcVideoComponent,
		AudioInputDeviceComponent,
		GetScenarioRecordingByIdPipe,
		EventContainerComponent,
		GetRoomCapabilityStringPipe,
		GetScenarioGroupNameByScenarioIdPipe,
		GetMarkerSetByIdPipe,
		GetVogSetByIdPipe,
		GetVopSetByIdPipe,
		GetChecklistByIdPipe,
		GetScenarioMediaSetByIdPipe,
		MediaCardListComponent,
		DetachableChangeDetectionDirective,
		BasicAdministrationMediaLibraryEditPageComponent,
		GetEventStateStringForCustomerPipe,
		ArrayFilterPipe,
		AudioChannelFilterPipe,
		GetEventByScenarioRecordingIdPipe,
		GetTrainingSessionbyIdPipe,
		GetUserByIdPipe,
		GetUsersByIdsPipe,
		AttachmentItemComponent,
		RoomLayoutSwitcherComponent,
		IsDefaultIconPipe,
		GetCameraByIdPipe,
		BasicAdminSettingsTableComponent,
		TypedMatCellDefDirective,
		SimAdminSettingsInputToLabelTransformerComponent,
		SimAdminSettingsSelectToLabelTransformerComponent,
		GetEventStateStringPipe,
		SimAdminSettingsInputListToEditableListComponent,
		SimAdminSettingsSliderToEditableSliderComponent,
		ScenarioArchiveExportsPanelComponent,
		SimBaseChipInputComponent,
		SimUserChipInputComponent,
		SimRoomChipInputComponent,
		SimScenarioChipInputComponent,
		SimRoomGroupChipInputComponent,
		SimBookmarksChipInputComponent,
		SimChecklistChipInputComponent,
		SimScenarioMediaChipInputComponent,
		SimVogChipInputComponent,
		SimVopChipInputComponent,
		FileSizePipe,
		CountdownTimerComponent,
		SimScenarioGroupChipInputComponent,
		FilterInvisibleAudioChannelsPipe,
		BaseAdministrationCheckedActionTableComponent,
		GetTrainingRoomGroupByIdPipe,
		ChecklistItemComponent,
		RoomLegendDisplayComponent,
		UnsupportedTabletTemplateComponent,
		FilterDefaultBookmarksPipe,
		LabeledInfoComponent,
		BatteryStatusIconComponent,
		SimAdminSettingsEnumMultipleSelectToLabelTransformerComponent,
		BatteryStateComponent,
		AssessableGroupPanelComponent,
		AssessmentItemComponent,
		AssessmentPointInputComponent,
		AssessmentPaneComponent,
		ArrayWithoutPipe,
		TableColumnSearchComponent,
		WrapFnPipe,
		RichTextEditorControlComponent,
		RichTextEditorViewComponent,
		ScenarioArchiveFilesetsPanelComponent,
		GetEventByIdPipe,
		ChecklistPanelComponent,
		SimEventChipInputComponent,
		HorizontalUserBubblesComponent,
		OperatorChecklistPanelComponent,
		SimTagsChipInputComponent,
		SimAssessmentTemplateChipInputComponent,
		SimAdminSettingsInputChipListComponent,
		SimTagsChipViewComponent,
		AssessableAdminIndexComponent,
		ThreeColumnLayoutComponent,
		MultipleChoiceItemComponent,
		SingleChoiceItemComponent,
		AssessableItemComponent,
		YesNoItemComponent,
		RatingItemComponent,
		FreeAnswerItemComponent,
		NoteAreaComponent,
		SimAutocompleteComponent,
		SimChipAutocompleteComponent,
		OperatorPanelComponent,
		AssessmentExaminerNoteComponent,
		SimAdminSettingsScrollEntriesConComponent,
		GetExamFlowByIdPipe,
		EventStatusWidgetComponent,
		CompetenceCategoryEditComponent,
		CompetenceCategoryAnswerComponent,
		AssessableAnswerItemComponent,
		ItemScoreComponent,
		AssessableGroupComponent,
		AssessmentEditPanelComponent,
		AssessmentAnswerPanelComponent,
		AssessmentAnswerPanelComponent,
		ClinicalVignetteViewComponent,
		DrawerFormFieldComponent,
		OldParticipantDetailsComponent,
		AssessmentAnswerSingleItemPanelComponent,
		ClockTimeInputComponent,
		TwoColumnLayoutComponent,
		GetUserRecordingAssessmentByIdPipe,
		ParticipantDeviceExaminerAssessmentDisplayComponent,
		ParticipantDeviceStudentAssessmentDisplayComponent,
		AdministrationTableComponent,
		AdministrationTableBulkActionsComponent,
		AdministrationTablePaginatorComponent,
		AdministrationTableAdvancedSearchComponent,
		AdministrationTableAppliedFiltersComponent,
		AdministrationTablePageLayoutComponent,
		AdministrationTableSimpleSearchComponent,
		TimerDisplayComponent,
		LmsPublishControlComponent,
	],
	exports: [
		EventStatusTagComponent,
		FileListItemComponent,
		NewListItemComponent,
		TimeInputComponent,
		PhaseNavButtonComponent,
		NumpadComponent,
		TranslateModule,
		FormsModule,
		RecordingArchiveCameraComponent,
		DynamicScriptDirective,
		RoundPipe,
		OrderByPipe,
		MjpegImageComponent,
		ReactiveFormsModule,
		ToolbarMenuComponent,
		ClientAudioDeviceComponent,
		NgxMaterialTimepickerModule,
		UserImageInitialsComponent,
		UserImagePipe,
		RecordingTimerComponent,
		AudioPlayerComponent,
		CameraViewModeSelectorComponent,
		RouterModule,
		FocusDirective,
		MediaPlayDirective,
		IpBeautifySpacesPipe,
		UserFullNameComponent,
		SimFilterPipe,
		LayoutModule,
		OldPaginationComponent,
		MarkerSetPaneComponent,
		TrainingRoomNavigationPanelComponent,
		SlideToggleDirective,
		SlidePanelComponent,
		NotFoundComponent,
		IconImageComponent,
		MmssPipe,
		MinsecPipe,
		ChecklistPaneComponent,
		BookmarkPaneComponent,
		DurationLineContainerComponent,
		DurationLineNormalComponent,
		DurationLineReversedComponent,
		DebugContainerComponent,
		SlidePanelComponent,
		FadeToggleDirective,
		AudioViewerComponent,
		DocumentViewerComponent,
		PresentationViewerComponent,
		CameraViewerComponent,
		ImageViewerComponent,
		MediaViewerComponent,
		DigitalSignageViewerComponent,
		ClinicalVignetteComponent,
		WelcomeComponent,
		VideoViewerComponent,
		LoadingDotsComponent,
		MaterialModule,
		SlideNavigationPanelComponent,
		FilterSessionByDatePipe,
		AmplitudeDirective,
		TrainingRoomButtonComponent,
		RecorderCameraComponent,
		BaseCameraLayout,
		SimSlideToggleIfElseComponent,
		OldPanelComponent,
		UserListItemComponent,
		MarkerFilterPipe,
		MediaFilterPipe,
		CameraViewModeToIndexPipe,
		CastToAudioInputChannelsPipe,
		AppTileComponent,
		EventDetailSessionComponent,
		EventSelectionComponent,
		ScenarioRecordingPlayerComponent,
		FileUploadComponent,
		PptxViewerComponent,
		DictionaryPipe,
		FilterEmptyScenarioRecordingsPipe,
		FilterEmptyGroupedParticipantPipe,
		NotnullPipe,
		MultiSwitchCaseDirective,
		OldSideActionBarComponent,
		EventCalendarComponent,
		AudioChannelControlPanelComponent,
		TabletAudioChannelControlPanelComponent,
		SystemActionsComponent,
		EnvironmentOverlayComponent,
		AddLeadingZeroPipe,
		MediaLibraryPanelComponent,
		WebSocketDatePipe,
		VarDirective,
		RtspwebrtcVideoComponent,
		SimBaseChipInputComponent,
		SimUserChipInputComponent,
		SimRoomChipInputComponent,
		SimScenarioChipInputComponent,
		SimRoomGroupChipInputComponent,
		SimBookmarksChipInputComponent,
		SimChecklistChipInputComponent,
		SimVopChipInputComponent,
		SimScenarioMediaChipInputComponent,
		SimScenarioGroupChipInputComponent,
		SimVogChipInputComponent,
		AudioInputDeviceComponent,
		PanelContentWarningContainerComponent,
		GetScenarioByIdPipe,
		GetScenarioRecordingByIdPipe,
		EventContainerComponent,
		GetRoomCapabilityStringPipe,
		GetScenarioGroupNameByScenarioIdPipe,
		GetMarkerSetByIdPipe,
		GetVogSetByIdPipe,
		GetVopSetByIdPipe,
		GetChecklistByIdPipe,
		GetScenarioMediaSetByIdPipe,
		MediaCardListComponent,
		DetachableChangeDetectionDirective,
		BasicAdministrationMediaLibraryEditPageComponent,
		WrapFnPipe,
		GetEventStateStringForCustomerPipe,
		GetEventStateStringPipe,
		GetScenarioRecordingStateStringForCustomerPipe,
		ArrayFilterPipe,
		FileSizePipe,
		AxisMediaStreamLibraryVideoComponent,
		AudioChannelFilterPipe,
		MediaCardAudioComponent,
		MediaCardImageComponent,
		MediaCardVideoComponent,
		MediaCardPdfComponent,
		MediaCardPowerPointComponent,
		GetEventByScenarioRecordingIdPipe,
		GetTrainingSessionbyIdPipe,
		GetUserByIdPipe,
		GetUsersByIdsPipe,
		AttachmentItemComponent,
		RoomLayoutSwitcherComponent,
		IsDefaultIconPipe,
		GetCameraByIdPipe,
		BasicAdminSettingsTableComponent,
		TypedMatCellDefDirective,
		SimAdminSettingsInputToLabelTransformerComponent,
		NgxJsonViewerModule,
		SimAdminSettingsSelectToLabelTransformerComponent,
		SimAdminSettingsInputListToEditableListComponent,
		SimAdminSettingsSliderToEditableSliderComponent,
		ScenarioArchiveExportsPanelComponent,
		CountdownTimerComponent,
		FilterInvisibleAudioChannelsPipe,
		BaseAdministrationCheckedActionTableComponent,
		GetTrainingRoomGroupByIdPipe,
		TableVirtualScrollModule,
		RoomLegendDisplayComponent,
		UnsupportedTabletTemplateComponent,
		FilterDefaultBookmarksPipe,
		LabeledInfoComponent,
		SimAdminSettingsEnumMultipleSelectToLabelTransformerComponent,
		AssessmentPaneComponent,
		ArrayWithoutPipe,
		TableColumnSearchComponent,
		AssessmentItemComponent,
		ScenarioArchiveFilesetsPanelComponent,
		RichTextEditorControlComponent,
		RichTextEditorViewComponent,
		GetEventByIdPipe,
		ChecklistPanelComponent,
		SimEventChipInputComponent,
		HorizontalUserBubblesComponent,
		SimAssessmentTemplateChipInputComponent,
		OperatorChecklistPanelComponent,
		SimTagsChipInputComponent,
		SimAdminSettingsInputChipListComponent,
		SimTagsChipViewComponent,
		AssessableAdminIndexComponent,
		ThreeColumnLayoutComponent,
		SimAutocompleteComponent,
		SimChipAutocompleteComponent,
		OperatorPanelComponent,
		AssessmentExaminerNoteComponent,
		SimAdminSettingsScrollEntriesConComponent,
		GetExamFlowByIdPipe,
		SimDurationInputComponent,
		EventStatusWidgetComponent,
		AssessmentEditPanelComponent,
		AssessmentAnswerPanelComponent,
		AssessmentAnswerSingleItemPanelComponent,
		ClinicalVignetteViewComponent,
		DrawerFormFieldComponent,
		DrawerFormFieldComponent,
		OldParticipantDetailsComponent,
		ClockTimeInputComponent,
		TwoColumnLayoutComponent,
		GetUserRecordingAssessmentByIdPipe,
		ParticipantDeviceExaminerAssessmentDisplayComponent,
		ParticipantDeviceStudentAssessmentDisplayComponent,
		AdministrationTableComponent,
		AdministrationTableBulkActionsComponent,
		AdministrationTablePaginatorComponent,
		AdministrationTableAdvancedSearchComponent,
		AdministrationTableAppliedFiltersComponent,
		AdministrationTablePageLayoutComponent,
		AdministrationTableSimpleSearchComponent,
		TimerDisplayComponent,
		RatingItemComponent,
		RecorderLayoutSingleComponent,
		NgAsDirective,
		NgAsPipe,
		LmsPublishControlComponent,
	],
	imports: [
		NgAsDirective,
		NgAsPipe,
		CommonModule,
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule,
		LayoutModule,
		NgxMaterialTimepickerModule,
		MaterialModule,
		NgxJsonViewerModule,
		TableVirtualScrollModule,
		QuillModule.forRoot({
			customModules: [
				{
					implementation: OnClickModule,
					path: "modules/onclick",
				},
			],
		}),
		FullCalendarModule,
		StripHtmlPipe,
		SafeHtmlComponent,
		NgxEchartsModule.forRoot({
			echarts,
		}),
		EventDaySelectorComponent,
		TypeGuardPipe,
		AssessableItemTemplateDirective,
		MseVideoComponent,
		BasicPageLayoutComponent,
		EnumLabelPipe,
		ListItemComponent,
		DictionaryPipe,
		ComponentLoadingContainerComponent,
		LogoComponent,
		StopPropagationDirective,
		SessionCardComponent,
		HasPermissionDirective,
		HasDataPermissionDirective,
		GetScenarioRecordingStateStringForCustomerPipe,
		GetRecordingMappingIdPipe,
		AnimatedMaterialIconComponent,
		BusyDirective,
		SimButton,
		GetScenarioByIdPipe,
		ExamDevicePanelComponent,
		AssessmentExamIndexComponent,
		AssessmentExamInfoHeaderComponent,
		LabelledContainerComponent,
		ActionMenuComponent,
		OldPaginationComponent,
		NewListItemComponent,
		UserListItemComponent,
		SideActionBarComponent,
		SegmentedButtonComponent,
		ButtonSegmentComponent,
		DurationLabelPipe,
		TimeRangeLabelPipe,
		SimDurationInputComponent,
		PanelContentWarningContainerComponent,
		AudioElementComponent,
		MarqueeTextComponent,
		RecordingStatusTagComponent,
		ScheduledSoundOffsetPipe,
	],
	providers: [
		DatePipe,
		AddLeadingZeroPipe,
		FilterParticipantsPipe,
		TransformParticipantsToSimUsersPipe,
		{provide: DateAdapter, useClass: CustomDateAdapter},
		{
			provide: MatPaginatorIntl,
			deps: [TranslateService],
			useFactory: createMatPaginatorLocalization,
		},
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class SharedModule {}
